import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ArrowRight, CreditCard, LayoutDashboard, X, PlayCircle } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { WelcomeTicket, welcomeLine } from '@/lib/activation';

/**
 * "Payment successful. Your Basic membership is active and 5 lead credits are
 * ready." — shown on the Projects page (and the dashboard) right after the
 * customer returns from checkout, with the three actions the brief asks for:
 * Browse Projects · View My Credits · Go to Dashboard.
 *
 * The wording comes from welcomeLine() so the plan name and the credit count
 * are derived from the shared plan catalog rather than retyped here.
 */

interface Props {
  ticket: WelcomeTicket;
  onDismiss: () => void;
  /** Re-open the 5-step walkthrough. */
  onReplayTour?: () => void;
  /** Hide the "Browse Projects" button when already on Projects. */
  onProjects?: boolean;
}

const PostPurchaseBanner: React.FC<Props> = ({ ticket, onDismiss, onReplayTour, onProjects }) => {
  const { lang, path } = useI18n();

  const btn =
    'inline-flex min-h-[48px] items-center justify-center gap-2 rounded-xl px-5 text-sm font-bold transition-colors';

  return (
    <div className="mb-8 overflow-hidden rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-5 sm:p-6">
      <div className="flex items-start gap-4">
        <CheckCircle2 className="mt-0.5 h-7 w-7 shrink-0 text-emerald-600" />
        <div className="flex-1">
          <h2 className="text-lg font-extrabold leading-snug text-emerald-900 sm:text-xl">
            {welcomeLine(ticket, lang)}
          </h2>
          <p className="mt-1.5 text-sm font-semibold text-emerald-800">
            {lang === 'es'
              ? '1 crédito = 1 lead. Elija un proyecto abajo y toque Desbloquear.'
              : '1 credit = 1 lead. Pick a project below and tap Unlock.'}
          </p>

          <div className="mt-4 flex flex-col gap-2.5 sm:flex-row sm:flex-wrap">
            {!onProjects && (
              <Link to={path('/jobs')} className={`${btn} bg-orange-500 text-white hover:bg-orange-600`}>
                {lang === 'es' ? 'Ver Proyectos' : 'Browse Projects'} <ArrowRight className="h-4 w-4" />
              </Link>
            )}
            <Link
              to={path('/dashboard')}
              className={`${btn} border-2 border-emerald-600 bg-white text-emerald-800 hover:bg-emerald-100`}
            >
              <CreditCard className="h-4 w-4" />
              {lang === 'es' ? 'Ver Mis Créditos' : 'View My Credits'}
            </Link>
            <Link
              to={path('/dashboard')}
              className={`${btn} border-2 border-slate-300 bg-white text-slate-700 hover:bg-white`}
            >
              <LayoutDashboard className="h-4 w-4" />
              {lang === 'es' ? 'Ir al Panel' : 'Go to Dashboard'}
            </Link>
            {onReplayTour && (
              <button type="button" onClick={onReplayTour} className={`${btn} text-emerald-800 hover:bg-emerald-100`}>
                <PlayCircle className="h-4 w-4" />
                {lang === 'es' ? 'Ver la guía' : 'Show me how'}
              </button>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label={lang === 'es' ? 'Cerrar' : 'Dismiss'}
          className="rounded-lg p-2 text-emerald-600 hover:bg-emerald-100"
        >
          <X className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
};

export default PostPurchaseBanner;
